import React, { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { ExternalLink, ArrowRight, TrendingUp, X, Calendar, Users, Target, Award } from 'lucide-react'

const Projects: React.FC = () => {
  const [selectedProject, setSelectedProject] = useState<typeof projects[0] | null>(null)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const projects = [
    {
      title: "GoGMGo - Connected F&B Management Platform",
      description: "Led the development of a comprehensive, cloud-based F&B management ecosystem that combines POS, accounting, labor management, and inventory systems into a unified platform. The solution serves as a complete end-to-end business management tool for restaurants and food service businesses.",
      category: "Enterprise F&B Platform",
      impact: "Complete business automation, 1-4% COGS improvement, streamlined operations",
      technologies: ["Cloud Platform", "iPad POS", "ERP Integration", "Mobile Apps", "Inventory Management", "Accounting", "HR Management", "Analytics"],
      image: "/gogmgo-logo.svg",
      link: "https://www.gogmgo.com/",
      github: "#",
      featured: true,
      duration: "2021 - 2023",
      teamSize: "25+ members",
      keyMetrics: ["Complete business automation", "1-4% COGS improvement", "Streamlined operations", "Multi-module integration"],
      challenges: ["Complex multi-module integration", "Real-time data synchronization", "Offline functionality", "Scalable cloud architecture"],
      achievements: ["Delivered comprehensive F&B management platform", "Integrated POS, ERP, HR, and inventory systems", "Achieved seamless cloud-based operations", "Enabled offline POS functionality"],
      methodology: "Agile development with continuous integration and cloud-first architecture",
      submodules: [
        {
          name: "GOPOSGO",
          description: "Complete POS application suite with printer integration, multiple payment solutions, loyalty platforms, and table reservation systems. Integrated with third-party providers including delivery platforms (GrabFood, Deliveroo, FoodPanda), accounting (Xero), hardware (Epson, Bluetooth barcode scanners), and fintech solutions (RedDot, MSwipe, Fiserv).",
          technologies: ["POS System", "Payment Integration", "Loyalty Platforms", "Table Reservations", "Third-party APIs", "Hardware Integration", "Fintech Solutions"]
        },
        {
          name: "GODATAGO",
          description: "AI analytics platform providing real-time dynamic insights with an AI chatbot that answers customer questions using real sales data generated by their POS system.",
          technologies: ["AI Analytics", "Real-time Insights", "AI Chatbot", "Data Analytics", "Machine Learning", "Sales Intelligence"]
        },
        {
          name: "ORDRR",
          description: "QR-based ordering platform enabling customers to place orders directly from their tables using mobile devices.",
          technologies: ["QR Technology", "Mobile Ordering", "Table-side Service", "Digital Menus", "Contactless Ordering"]
        },
        {
          name: "Online Order Platform",
          description: "Comprehensive online ordering platform for restaurants on the system, enabling digital ordering and delivery management.",
          technologies: ["Online Ordering", "Delivery Management", "Digital Menus", "Payment Processing", "Order Tracking"]
        },
        {
          name: "GOERPGO",
          description: "Complete web-based ERP suite helping restaurants manage complete operations including sales, menu engineering, finances, HR, inventory, and logistics.",
          technologies: ["ERP System", "Menu Engineering", "Financial Management", "HR Management", "Inventory Management", "Logistics", "Web Platform"]
        }
      ]
    },
    {
      title: "OroClean - Multi-Brand ERP & Website Platform",
      description: "Built dynamic multi-lingual websites for all OroClean brands and established a comprehensive one-stop ERP system to manage all operations. The platform provides centralized management for multiple brand websites with unified backend operations.",
      category: "Multi-Brand ERP Platform",
      impact: "Centralized brand management, unified operations, multi-language support",
      technologies: ["Multi-Lingual Websites", "ERP System", "Brand Management", "Content Management", "Database Integration", "API Development", "Cloud Hosting"],
      image: "/oroclean-logo.svg",
      link: "https://www.oroclean.com",
      github: "#",
      featured: true,
      duration: "2020 - 2022",
      teamSize: "15+ members",
      keyMetrics: ["Multi-brand website management", "Unified ERP operations", "Multi-language support", "Centralized brand control"],
      challenges: ["Multi-brand architecture", "Language localization", "ERP integration complexity", "Scalable content management"],
      achievements: ["Delivered multi-brand website platform", "Built comprehensive ERP system", "Implemented multi-language support", "Achieved unified operations management"],
      methodology: "Agile development with multi-brand architecture and localization focus"
    },
    {
      title: "Zvoid - White Label App Development",
      description: "Developed multiple applications as a white label partner for Zvoid, creating customizable solutions that can be rebranded and deployed for different clients. The platform enables rapid deployment of branded applications with consistent functionality.",
      category: "White Label Solutions",
      impact: "Rapid app deployment, consistent functionality, brand customization",
      technologies: ["White Label Platform", "Mobile App Development", "Brand Customization", "API Integration", "Multi-tenant Architecture", "Rapid Deployment"],
      image: "/zvoid-logo.png",
      link: "https://www.zvoid.com",
      github: "#",
      featured: true,
      duration: "2019 - 2021",
      teamSize: "12+ members",
      keyMetrics: ["Multiple white label apps", "Rapid deployment capability", "Brand customization", "Consistent functionality"],
      challenges: ["Multi-tenant architecture", "Brand customization flexibility", "Rapid deployment requirements", "Scalable platform design"],
      achievements: ["Built white label app platform", "Delivered multiple branded applications", "Achieved rapid deployment capability", "Maintained consistent functionality"],
      methodology: "Agile development with white label architecture and rapid deployment focus"
    },
    {
      title: "Coca-Cola - Event Management Applications",
      description: "Built comprehensive event management applications for Coca-Cola, enabling efficient planning, execution, and management of various events and campaigns. The platform provides tools for event coordination, attendee management, and real-time event tracking.",
      category: "Event Management Platform",
      impact: "Streamlined event management, improved coordination, enhanced attendee experience",
      technologies: ["Event Management", "Mobile Apps", "Real-time Tracking", "Attendee Management", "Event Coordination", "Campaign Management"],
      image: "/coca-cola-logo.svg",
      link: "https://www.coca-cola.com/xf/en",
      github: "#",
      featured: false,
      duration: "2018 - 2020",
      teamSize: "10+ members",
      keyMetrics: ["Streamlined event management", "Improved coordination", "Enhanced attendee experience", "Real-time event tracking"],
      challenges: ["Complex event workflows", "Real-time coordination", "Multi-location events", "Attendee engagement"],
      achievements: ["Delivered event management platform", "Improved event coordination", "Enhanced attendee experience", "Achieved real-time tracking"],
      methodology: "Agile development with event management focus and real-time capabilities"
    },
    {
      title: "Lasts Logistics - Fleet Management Platform",
      description: "Built a comprehensive logistics management platform with mobile apps for drivers, providing real-time location tracking, job status updates, and maintenance scheduling. The system ensures efficient fleet operations with compliance and maintenance tracking.",
      category: "Logistics & Fleet Management",
      impact: "Real-time fleet tracking, improved job management, compliance scheduling",
      technologies: ["Fleet Management", "Mobile Apps", "Real-time GPS", "Job Management", "Maintenance Scheduling", "Compliance Tracking", "Logistics Platform"],
      image: "/lasts-logistics-logo.svg",
      link: "#",
      github: "#",
      featured: false,
      duration: "2019 - 2021",
      teamSize: "18+ members",
      keyMetrics: ["Real-time fleet tracking", "Improved job management", "Compliance scheduling", "Maintenance tracking"],
      challenges: ["Real-time GPS integration", "Mobile app reliability", "Compliance management", "Maintenance scheduling"],
      achievements: ["Built logistics management platform", "Delivered driver mobile apps", "Implemented real-time tracking", "Achieved compliance management"],
      methodology: "Agile development with real-time tracking and mobile-first approach"
    },
    {
      title: "Shine Aviation - Flight Management Platform",
      description: "Built a comprehensive white label platform for managing aviation operations including flight paths, compliance tracking, maintenance schedules, flight logs, events, and risk management. The system ensures flights operate on time, well-managed, and risk-free.",
      category: "Aviation Management Platform",
      impact: "On-time flight operations, risk-free management, compliance tracking",
      technologies: ["Aviation Management", "Flight Planning", "Compliance Tracking", "Maintenance Scheduling", "Risk Management", "Flight Logs", "Real-time Monitoring"],
      image: "/shine-aviation-logo.png",
      link: "https://www.shineaviation.com.au",
      github: "#",
      featured: false,
      duration: "2020 - 2022",
      teamSize: "20+ members",
      keyMetrics: ["On-time flight operations", "Risk-free management", "Compliance tracking", "Maintenance scheduling"],
      challenges: ["Aviation compliance complexity", "Real-time flight monitoring", "Risk assessment algorithms", "Maintenance scheduling"],
      achievements: ["Built aviation management platform", "Implemented compliance tracking", "Achieved risk-free operations", "Maintained on-time performance"],
      methodology: "Agile development with aviation compliance focus and risk management"
    }
  ]

  const openProjectDetails = (project: typeof projects[0]) => {
    setSelectedProject(project)
    setIsModalOpen(true)
  }

  const closeModal = () => {
    setIsModalOpen(false)
    setSelectedProject(null)
  }

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  }

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6
      }
    }
  }

  return (
    <section id="projects" className="section-padding">
      <div className="container-custom">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Featured <span className="gradient-text">Projects</span>
          </h2>
                        <p className="text-xl text-gray-600 dark:text-gray-400 max-w-3xl mx-auto">
                Comprehensive portfolio showcasing leadership, technical expertise, and measurable business impact across diverse sectors
              </p>
        </motion.div>

        {/* Featured Projects Grid */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="mb-16"
        >
          <div className="grid lg:grid-cols-2 gap-8">
            {projects.map((project, index) => (
              <motion.div
                key={project.title}
                variants={itemVariants}
                className="group relative bg-white dark:bg-gray-800 rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-2"
              >
                {/* Project Image */}
                <div className="h-48 bg-gradient-to-br from-[#3eb3f0] to-[#3eb3f0]/80 dark:from-[#3eb3f0] dark:to-[#3eb3f0]/80 flex items-center justify-center border border-gray-200 dark:border-gray-600">
                  {project.image.startsWith('/') ? (
                    <img 
                      src={project.image} 
                      alt={`${project.title} logo`}
                      className="max-w-full max-h-full w-auto h-auto object-contain group-hover:scale-110 transition-transform duration-300"
                      style={{ maxHeight: '120px', maxWidth: '200px' }}
                    />
                  ) : (
                    <div className="text-8xl group-hover:scale-110 transition-transform duration-300">
                      {project.image}
                    </div>
                  )}
                </div>

                {/* Project Content */}
                <div className="p-6">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center space-x-2">
                      <span className="px-3 py-1 bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 text-sm font-medium rounded-full">
                        {project.category}
                      </span>
                      {project.submodules && project.submodules.length > 0 && (
                        <span className="px-2 py-1 bg-cyber-100 dark:bg-cyber-900/30 text-cyber-700 dark:text-cyber-400 text-xs font-medium rounded-full border border-cyber-200 dark:border-cyber-700">
                          {project.submodules.length} Modules
                        </span>
                      )}
                    </div>
                    <div className="flex space-x-2">
                      {project.link && project.link !== "#" && (
                        <motion.button
                          whileHover={{ scale: 1.1 }}
                          whileTap={{ scale: 0.9 }}
                          className="p-2 text-gray-400 hover:text-primary-500 transition-colors duration-200"
                          onClick={() => window.open(project.link, '_blank')}
                        >
                          <ExternalLink className="w-4 h-4" />
                        </motion.button>
                      )}
                    </div>
                  </div>

                  <h3 className="text-xl font-bold text-gray-800 dark:text-gray-200 mb-3 group-hover:text-primary-600 dark:group-hover:text-primary-400 transition-colors duration-200">
                    {project.title}
                  </h3>
                  
                  <p className="text-gray-600 dark:text-gray-400 mb-4 leading-relaxed">
                    {project.description}
                  </p>

                  {/* Impact Metrics */}
                  <div className="flex items-center space-x-4 mb-4">
                    <div className="flex items-center space-x-2 text-sm text-green-600 dark:text-green-400">
                      <TrendingUp className="w-4 h-4" />
                      <span>{project.impact}</span>
                    </div>
                  </div>

                  {/* Technologies */}
                  <div className="flex flex-wrap gap-2 mb-4">
                    {project.technologies.map((tech) => (
                      <span
                        key={tech}
                        className="px-2 py-1 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 text-xs rounded-md"
                      >
                        {tech}
                      </span>
                    ))}
                  </div>

                  <motion.button
                    onClick={() => openProjectDetails(project)}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    className="w-full bg-gradient-to-r from-cyber-600 via-cyber-500 to-cyber-600 hover:from-cyber-700 hover:via-cyber-600 hover:to-cyber-700 text-white font-semibold py-3 px-6 rounded-xl transition-all duration-300 shadow-lg hover:shadow-cyber-500/25 hover:scale-105 border border-cyber-500/20 group"
                  >
                    <span className="flex items-center justify-center">
                      <span className="mr-2">View Details</span>
                      <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-200" />
                    </span>
                  </motion.button>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Project Details Modal */}
        <AnimatePresence>
          {isModalOpen && selectedProject && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
              onClick={closeModal}
            >
              <motion.div
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.9, opacity: 0 }}
                transition={{ type: "spring", damping: 25, stiffness: 300 }}
                className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto"
                onClick={(e) => e.stopPropagation()}
              >
                {/* Modal Header */}
                <div className="p-6 border-b border-gray-200 dark:border-gray-700">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4">
                      {selectedProject.image.startsWith('/') ? (
                        <img 
                          src={selectedProject.image} 
                          alt={`${selectedProject.title} logo`}
                          className="w-24 h-24 object-contain"
                        />
                      ) : (
                        <div className="text-6xl">{selectedProject.image}</div>
                      )}
                      <div>
                        <h3 className="text-2xl font-bold text-gray-800 dark:text-gray-200">
                          {selectedProject.title}
                        </h3>
                        <p className="text-cyber-400 font-medium">{selectedProject.category}</p>
                      </div>
                    </div>
                    <button
                      onClick={closeModal}
                      className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-colors duration-200"
                    >
                      <X className="w-6 h-6 text-gray-500" />
                    </button>
                  </div>
                </div>

                {/* Modal Content */}
                <div className="p-6 space-y-6">
                  {/* Key Metrics */}
                  <div className="grid md:grid-cols-2 gap-6">
                    <div className="space-y-4">
                      <div className="flex items-center space-x-3">
                        <Calendar className="w-5 h-5 text-cyber-500" />
                        <span className="text-gray-600 dark:text-gray-400">
                          <strong>Duration:</strong> {selectedProject.duration}
                        </span>
                      </div>
                      <div className="flex items-center space-x-3">
                        <Users className="w-5 h-5 text-cyber-500" />
                        <span className="text-gray-600 dark:text-gray-400">
                          <strong>Team Size:</strong> {selectedProject.teamSize}
                        </span>
                      </div>
                      <div className="flex items-center space-x-3">
                        <Target className="w-5 h-5 text-cyber-500" />
                        <span className="text-gray-600 dark:text-gray-400">
                          <strong>Methodology:</strong> {selectedProject.methodology}
                        </span>
                      </div>
                    </div>
                    
                    <div className="space-y-4">
                      <h4 className="font-semibold text-gray-800 dark:text-gray-200 flex items-center">
                        <TrendingUp className="w-4 h-4 mr-2 text-green-500" />
                        Key Metrics
                      </h4>
                      <ul className="space-y-2">
                        {selectedProject.keyMetrics?.map((metric, index) => (
                          <li key={index} className="text-sm text-gray-600 dark:text-gray-400 flex items-center">
                            <div className="w-2 h-2 bg-green-500 rounded-full mr-3"></div>
                            {metric}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>

                  {/* Description */}
                  <div>
                    <h4 className="font-semibold text-gray-800 dark:text-gray-200 mb-3">Project Overview</h4>
                    <p className="text-gray-600 dark:text-gray-400 leading-relaxed">
                      {selectedProject.description}
                    </p>
                  </div>

                  {/* Challenges & Achievements */}
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <h4 className="font-semibold text-gray-800 dark:text-gray-200 mb-3 flex items-center">
                        <Target className="w-4 h-4 mr-2 text-orange-500" />
                        Key Challenges
                      </h4>
                      <ul className="space-y-2">
                        {selectedProject.challenges?.map((challenge, index) => (
                          <li key={index} className="text-sm text-gray-600 dark:text-gray-400 flex items-center">
                            <div className="w-2 h-2 bg-orange-500 rounded-full mr-3"></div>
                            {challenge}
                          </li>
                        ))}
                      </ul>
                    </div>
                    
                    <div>
                      <h4 className="font-semibold text-gray-800 dark:text-gray-200 mb-3 flex items-center">
                        <Award className="w-4 h-4 mr-2 text-yellow-500" />
                        Key Achievements
                      </h4>
                      <ul className="space-y-2">
                        {selectedProject.achievements?.map((achievement, index) => (
                          <li key={index} className="text-sm text-gray-600 dark:text-gray-400 flex items-center">
                            <div className="w-2 h-2 bg-yellow-500 rounded-full mr-3"></div>
                            {achievement}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>

                  {/* Technologies */}
                  <div>
                    <h4 className="font-semibold text-gray-800 dark:text-gray-200 mb-3">Technologies & Tools</h4>
                    <div className="flex flex-wrap gap-2">
                      {selectedProject.technologies.map((tech) => (
                        <span
                          key={tech}
                          className="px-3 py-1 bg-cyber-100 dark:bg-cyber-900/30 text-cyber-700 dark:text-cyber-300 text-sm rounded-full border border-cyber-200 dark:border-cyber-700"
                        >
                          {tech}
                        </span>
                      ))}
                    </div>
                  </div>

                  {/* Submodules - Only show if project has submodules */}
                  {selectedProject.submodules && selectedProject.submodules.length > 0 && (
                    <div>
                      <h4 className="font-semibold text-gray-800 dark:text-gray-200 mb-3 flex items-center">
                        <div className="w-5 h-5 bg-cyber-500 rounded-full mr-2 flex items-center justify-center">
                          <span className="text-white text-xs font-bold">M</span>
                        </div>
                        Platform Submodules
                      </h4>
                      <div className="space-y-4">
                        {selectedProject.submodules.map((submodule, index) => (
                          <div key={index} className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
                            <h5 className="font-semibold text-cyber-600 dark:text-cyber-400 mb-2 text-lg">
                              {submodule.name}
                            </h5>
                            <p className="text-gray-600 dark:text-gray-400 mb-3 text-sm leading-relaxed">
                              {submodule.description}
                            </p>
                            <div className="flex flex-wrap gap-2">
                              {submodule.technologies.map((tech) => (
                                <span
                                  key={tech}
                                  className="px-2 py-1 bg-cyber-50 dark:bg-cyber-900/20 text-cyber-600 dark:text-cyber-400 text-xs rounded-md border border-cyber-200 dark:border-cyber-700"
                                >
                                  {tech}
                                </span>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {/* Modal Footer */}
                <div className="p-6 border-t border-gray-200 dark:border-gray-700 flex justify-end space-x-3">
                  <button
                    onClick={closeModal}
                    className="px-6 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors duration-200"
                  >
                    Close
                  </button>
                  {selectedProject.link && selectedProject.link !== "#" && (
                    <button className="px-6 py-2 bg-cyber-600 text-white rounded-lg hover:bg-cyber-700 transition-colors duration-200 flex items-center">
                      <ExternalLink className="w-4 h-4 mr-2" />
                      View Project
                    </button>
                  )}
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </section>
  )
}

export default Projects
